import { useState, useEffect } from "react";
import axios from "../../api/axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import styles from './Register.module.css';
import { Snackbar } from "@mui/material";
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import { useDispatch } from "react-redux";
import { setCredentials } from "../../redux/authSlice";
import { formatNumber, stringToNumber } from "../../helpers/utils";

const USER_REGEX = /^[A-Za-z][A-Za-z0-9.\s-]{4,29}$/;
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{5,24}$/;
const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const REGISTER_URL = '/users';

const Register = () => {

    const dispatch = useDispatch();

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const surveyFromUrl = searchParams.get("survey");
    console.log(`surveyFromUrl: ${surveyFromUrl}`);

    const qualtricsSurveyId = surveyFromUrl || sessionStorage.getItem("qualtricsSurveyId");
    console.log(`qualtricsSurveyId: ${qualtricsSurveyId}`);

    const [user, setUser] = useState('');
    const [validName, setValidName] = useState(false);
    const [userFocus, setUserFocus] = useState(false);

    const [pwd, setPwd] = useState('');
    const [validPwd, setValidPwd] = useState(false);
    const [pwdFocus, setPwdFocus] = useState(false);

    const [matchPwd, setMatchPwd] = useState('');
    const [validMatch, setValidMatch] = useState(false);
    const [matchFocus, setMatchFocus] = useState(false);

    const [email, setEmail] = useState('');
    const [validEmail, setValidEmail] = useState(false);
    const [emailFocus, setEmailFocus] = useState(false);

    const [aum, setAum] = useState('');
    const [validAum, setValidAum] = useState(false);
    const [aumFocus, setAumFocus] = useState(false);

    const [showPasswords, setShowPasswords] = useState(false);

    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const [alertStatus, setAlertStatus] = useState({
        open: false
    });

    const isExternalUrl = (url) => {
        try {
            const urlObj = new URL(url, window.location.origin);
            return urlObj.origin !== window.location.origin;
        } catch {
            return false;
        }
    };

    const getRelativePath = (inputUrl) => {

        const origin = window.location.origin;
        const homepage = process.env.PUBLIC_URL || '';

        try {
            const url = new URL(inputUrl);

            if (!inputUrl.startsWith(origin)) {
                console.warn('URL does not match current origin');
                return '/';
            }

            let relativePath = url.pathname;
            if (homepage && relativePath.startsWith(homepage)) {
                relativePath = relativePath.substring(homepage.length);
            }

            return relativePath + url.search + url.hash;
        } catch (error) {
            console.error('Invalid URL:', error);
            return '';
        }
    }

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertStatus((prev) => {
            return { ...prev, open: false }
        });
    };

    const onAumChange = (n) => {
        n = n.replace(/[^0-9.]/g, "");
        const specialCase = (n.at(-1) === ".");
        const parts = n.split(".");
        let whole = parts.shift();
        const decimal = parts.join("");

        // Cap whole part to 4 digits
        if (whole.length > 4) {
            whole = whole.slice(0, 4);
        }

        let formatted;
        if ((decimal === "") && specialCase) {
            formatted = formatNumber(whole) + ".";
        } else if (decimal === "") {
            formatted = formatNumber(whole);
        } else {
            // Cap decimal to 2 places
            formatted = formatNumber(whole) + "." + decimal.slice(0, 2);
        }
        setAum(formatted);
    };

    useEffect(() => {
        setValidName(USER_REGEX.test(user));
    }, [user]);

    useEffect(() => {
        setValidEmail(EMAIL_REGEX.test(email));
    }, [email]);

    useEffect(() => {
        setValidPwd(PWD_REGEX.test(pwd));
        setValidMatch(pwd === matchPwd);
    }, [pwd, matchPwd]);

    useEffect(() => {
        const stripped = aum.replace(/[^0-9.]/g, "");
        const numericValue = parseFloat(stripped);
        setValidAum(!isNaN(numericValue) && numericValue > 0 && numericValue <= 9999.99);
    }, [aum]);

    useEffect(() => {
        setSubmitDisabled(!validName || !validEmail || !validPwd || !validMatch || !validAum);
    }, [validName, validEmail, validPwd, validMatch, validAum]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        const v1 = USER_REGEX.test(user);
        const v2 = PWD_REGEX.test(pwd);
        const v3 = EMAIL_REGEX.test(email);
        if (!v1 || !v2 || !v3 || !validAum) {
            return;
        }
        try {
            const response = await axios.post(REGISTER_URL,
                JSON.stringify({
                    name: user,
                    email: email,
                    password: pwd,
                    aum: stringToNumber(aum),
                    qualtricsSurveyId
                }),
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                }
            );

            console.log(JSON.stringify(response?.data));

            const accessToken = response?.data?.accessToken;
            const isAdmin = response?.data?.isAdmin;
            const userId = response?.data?.userId;
            const surveyUrl = response?.data?.surveyUrl;
            const showBenchmarkingTour = response?.data?.showBenchmarkingTour;
            const showBenchmarkingHint = response?.data?.showBenchmarkingHint;
            const showInsightsTour = response?.data?.showInsightsTour;
            const profileComplete = response?.data?.profileComplete;
            dispatch(setCredentials({ email, userId, isAdmin, accessToken, showBenchmarkingTour, showBenchmarkingHint, showInsightsTour, profileComplete }));

            const redirectPath = sessionStorage.getItem("redirectAfterAuth");
            sessionStorage.removeItem("redirectAfterAuth");
            sessionStorage.removeItem("qualtricsSurveyId");

            if (surveyUrl) {
                console.log(`surveyUrl: ${surveyUrl} isExternalUrl: ${isExternalUrl(surveyUrl)} `);
                if (isExternalUrl(surveyUrl)) {
                    window.location.href = surveyUrl;
                } else {
                    const relativePath = getRelativePath(surveyUrl);
                    console.log(`relativePath: ${relativePath}`);
                    navigate(relativePath);
                }
            } else if (redirectPath) {
                navigate(redirectPath);
            } else {
                navigate("/");
            }

        } catch (err) {
            let message;
            if (!err?.response) {
                message = 'Error: No Server Response';
            } else if (err.response?.status === 409) {
                message = 'That email address is already registered';
            } else {
                message = 'Error: Registration Failed';
            }
            setIsLoading(false);
            setAlertStatus({
                open: true,
                severity: "error",
                message: message
            });
        }
    };

    const tooltipSlotProps = {
        popper: {
            sx: { zIndex: 9999 },
        },
        tooltip: {
            className: styles.validationTooltip,
        },
        arrow: {
            sx: { color: '#7d1327' },
        },
    };

    return (
        <section className={styles.section}>
            <h1>Register</h1>
            <form onSubmit={handleSubmit}>
                <div className={styles.inputWrapper}>
                    <label htmlFor="name">Name:</label>
                    <Tooltip
                        title={
                            <span>
                                4 to 30 characters.<br /><br />
                                Must begin with a letter.<br /><br />
                                Letters, numbers, hyphens, and periods allowed.
                            </span>
                        }
                        open={userFocus && user !== '' && !validName}
                        placement="right"
                        arrow
                        disableHoverListener
                        disableFocusListener
                        disableTouchListener
                        slotProps={tooltipSlotProps}
                    >
                        <input
                            type="text"
                            id="name"
                            className={(validName || !user) ? "" : styles.invalid}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            onChange={(e) => setUser(e.target.value)}
                            value={user}
                            required
                            aria-invalid={validName ? "false" : "true"}
                            onFocus={() => setUserFocus(true)}
                            onBlur={() => setUserFocus(false)}
                        />
                    </Tooltip>
                </div>

                <div className={styles.inputWrapper}>
                    <label htmlFor="email">Email:</label>
                    <Tooltip
                        title="Must be a valid email address."
                        open={emailFocus && email !== '' && !validEmail}
                        placement="right"
                        arrow
                        disableHoverListener
                        disableFocusListener
                        disableTouchListener
                        slotProps={tooltipSlotProps}
                    >
                        <input
                            type="email"
                            id="email"
                            className={(validEmail || !email) ? "" : styles.invalid}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            onChange={(e) => setEmail(e.target.value)}
                            value={email}
                            required
                            aria-invalid={validEmail ? "false" : "true"}
                            onFocus={() => setEmailFocus(true)}
                            onBlur={() => setEmailFocus(false)}
                        />
                    </Tooltip>
                </div>

                <div className={styles.inputWrapper}>
                    <label htmlFor="aum">AUM ($millions):</label>
                    <Tooltip
                        title={
                            <span>
                                Enter your AUM in millions.<br /><br />
                                Valid range: $1M to $9,999.99M.<br /><br />
                                Example: enter 250 for $250 million.
                            </span>
                        }
                        open={aumFocus && !validAum}
                        placement="right"
                        arrow
                        disableHoverListener
                        disableFocusListener
                        disableTouchListener
                        slotProps={tooltipSlotProps}
                    >
                        <input
                            type="text"
                            id="aum"
                            className={`${styles.currency} ${(!validAum && aum !== '') ? styles.invalid : ''}`}
                            autoComplete="one-time-code"
                            onChange={(e) => onAumChange(e.target.value)}
                            onFocus={(e) => { setAumFocus(true); e.target.select(); }}
                            onBlur={() => setAumFocus(false)}
                            value={aum}
                            required
                            maxLength={20}
                            size={20}
                            aria-invalid={validAum ? "false" : "true"}
                        />
                    </Tooltip>
                </div>

                <div className={styles.inputWrapper}>
                    <label htmlFor="password">Password:</label>
                    <Tooltip
                        title={
                            <span>
                                5 to 24 characters.<br /><br />
                                Must include uppercase and lowercase letters, a number and a special character.<br /><br />
                                Allowed special characters:<br />! @ # $ %
                            </span>
                        }
                        open={pwdFocus && !validPwd}
                        placement="right"
                        arrow
                        disableHoverListener
                        disableFocusListener
                        disableTouchListener
                        slotProps={tooltipSlotProps}
                    >
                        <input
                            type={showPasswords ? "text" : "password"}
                            id="password"
                            className={(validPwd || !pwd) ? "" : styles.invalid}
                            onChange={(e) => setPwd(e.target.value)}
                            value={pwd}
                            required
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            aria-invalid={validPwd ? "false" : "true"}
                            onFocus={() => setPwdFocus(true)}
                            onBlur={() => setPwdFocus(false)}
                        />
                    </Tooltip>
                </div>

                <div className={styles.inputWrapper}>
                    <label htmlFor="confirm_pwd">Confirm Password:</label>
                    <Tooltip
                        title="Must match the first password."
                        open={matchFocus && !validMatch}
                        placement="right"
                        arrow
                        disableHoverListener
                        disableFocusListener
                        disableTouchListener
                        slotProps={tooltipSlotProps}
                    >
                        <input
                            type={showPasswords ? "text" : "password"}
                            id="confirm_pwd"
                            className={(validMatch || !matchPwd) ? "" : styles.invalid}
                            onChange={(e) => setMatchPwd(e.target.value)}
                            value={matchPwd}
                            required
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            aria-invalid={validMatch ? "false" : "true"}
                            onFocus={() => setMatchFocus(true)}
                            onBlur={() => setMatchFocus(false)}
                        />
                    </Tooltip>
                </div>

                <div className={styles.checkboxAndLabel}>
                    <input
                        type="checkbox"
                        id="password_visibility"
                        name="password_visibility"
                        onChange={(e) => setShowPasswords(e.target.checked)}
                    />
                    <label htmlFor="password_visibility">Show passwords</label>
                </div>

                <button
                    className={styles.submitButton}
                    disabled={submitDisabled || isLoading}
                >
                    {isLoading ? "Signing Up..." : "Sign Up"}
                </button>
            </form>
            <nav>
                <Link to="/login">Sign in</Link>
            </nav>
            <Snackbar open={alertStatus.open} autoHideDuration={6000} onClose={handleClose}>
                <Alert onClose={handleClose} variant="filled" severity={alertStatus.severity} sx={{ width: '100%' }}>{alertStatus.message}</Alert>
            </Snackbar>

        </section>

    );
};

export default Register;